<template>
  <lb-drawer
    :value="value"
    heading="Send Status Summary Email"
    Maxwidth="850"
    @input="$emit('input', $event)"
    data-testid="summary-email-dialog"
  >
    <template v-slot:body>
      <div class="summary-email-dialog__form" data-testid="summary-email-dialog__form">
        <!-- To Field -->
        <div class="summary-email-dialog__field mb-4">
        <div data-testid="summary-email-dialog__selected-count-section" class="d-flex align-center dataCount gap-120 mb-6">
        <h6 data-testid="summary-email-dialog__selected-count-label" class="font-weight-medium count-label">
           Selected
        </h6>
        <h3 data-testid="summary-email-dialog__selected-count-value" class="font-weight-medium">
            {{ selectedItems.length }}
        </h3>
    </div>

          <label class="summary-email-dialog__label font-weight-bold mb-1 d-block">To</label>
          <v-combobox
            v-model="toEmails"
            :items="emailDropdownItems"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            placeholder="Enter email addresses"
            :hide-details="!toError"
            :error-messages="toError"
            data-testid="summary-email-dialog__to-input"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                small
                close
                @click:close="removeToEmail(index)"
                :key="'to-' + index"
              >
                {{ item }}
              </v-chip>
            </template>
          </v-combobox>
        </div>

        <!-- CC Field -->
        <div class="summary-email-dialog__field mb-4">
          <label class="summary-email-dialog__label font-weight-bold mb-1 d-block">CC</label>
          <v-combobox
            v-model="ccEmails"
            :items="emailDropdownItems"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            placeholder="Enter CC email addresses"
            :hide-details="!ccError"
            :error-messages="ccError"
            data-testid="summary-email-dialog__cc-input"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                small
                close
                @click:close="removeCcEmail(index)"
                :key="'cc-' + index"
              >
                {{ item }}
              </v-chip>
            </template>
          </v-combobox>
        </div>

        <!-- Subject -->
        <div class="summary-email-dialog__field mb-4">
             <label class="summary-email-dialog__label font-weight-bold mb-1 d-block">Subject*</label>
          <lb-string
            v-model="subject"
            label=""
            :error="subjectError"
            @input="subjectError = ''"
            data-testid="summary-email-dialog__subject"
          />
        </div>

        <!-- Email Body -->
        <div class="summary-email-dialog__field">
          <label class="summary-email-dialog__label font-weight-bold mb-1 d-block">Email Body</label>
          <v-textarea
            v-model="emailBody"
            outlined
            rows="14"
            auto-grow
            hide-details="auto"
            placeholder="Enter email body"
            data-testid="summary-email-dialog__body-input"
          ></v-textarea>
        </div>
      </div>
    </template>

    <template v-slot:actions>
         <v-chip class="noTransform ru-info-chip gap-2 align-center">
              <span><v-icon>mdi-information-outline</v-icon> Recipients will be saved for future suggestions.</span>
            </v-chip>
      <v-spacer></v-spacer>
      <base-button
        label="Cancel"
        secondary
        @click="handleCancel"
        data-testid="summary-email-dialog__cancel-btn"
      />
      <base-button
        label="Send Email"
        primary
        image="/img/icons/SendIConWhite.svg"
        :loading="loading"
        :disabled="!isFormValid"
        @click="handleSend"
        class="ml-2 EmailBtn"
        data-testid="summary-email-dialog__send-btn"
      />
    </template>
  </lb-drawer>
</template>

<script>
const DEFAULT_EMAIL_BODY = `Hello,

As part of the ongoing review/close process, please find below the latest status of the information requests. A detailed summary of the selected requests has been attached for your reference.

Action Required:
- Kindly review the pending items highlighted in the attached file
- Provide the required information/documents at the earliest against each of the request
- If any items have already been addressed, please disregard or share an update accordingly in the relevant request
- If you have any questions, need clarification, or require additional context on any of the requests, please feel free to reach out.

We appreciate your timely support in helping us complete this process efficiently.

Best regards,`;

export default {
  name: 'SummaryEmailDialog',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    suggestedContacts: {
      type: Array,
      default: () => [],
    },
    infoOwnerList: {
      type: Array,
      default: () => [],
    },
    emailTemplate: {
      type: Object,
      default: () => ({ subject: '', body: '', placeholders: [] }),
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    projectName: {
      type: String,
      default: '',
    },
    userName: {
      type: String,
      default: '',
    },
    userEmail: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      toEmails: [],
      ccEmails: [],
      subject: '',
      emailBody: DEFAULT_EMAIL_BODY,
      toError: '',
      ccError: '',
      subjectError: '',
    };
  },
  computed: {
    emailDropdownItems() {
      const isEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
      const suggested = (this.suggestedContacts || []).filter(v => v && isEmail(v));
      const ownerEmails = (this.infoOwnerList || [])
        .map(owner => owner.email || '')
        .filter(v => v && isEmail(v));
      return [...new Set([...suggested, ...ownerEmails])];
    },
    isFormValid() {
      return this.toEmails.length > 0 && this.subject.trim().length > 0 && this.emailBody.trim().length > 0;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetForm();
      }
    },
    toEmails(val) {
      if (val && val.length > 0) {
        this.toError = '';
      }
    },
    ccEmails(val) {
      if (val && val.length > 0) {
        this.ccError = '';
      }
    },
  },
  methods: {
    replacePlaceholders(text) {
      if (!text) return text;
      const today = this.$nova?.formatDate ? this.$nova.formatDate(new Date()) : new Date().toLocaleDateString();
      return text
        .replace(/\{\{project_name\}\}/gi, this.projectName || '')
        .replace(/\{\{current_date\}\}/gi, today)
        .replace(/\{\{sender_name\}\}/gi, this.userName || '')
        .replace(/\{\{sender_email\}\}/gi, this.userEmail || '')
        .replace(/\{\{request_count\}\}/gi, String(this.selectedItems.length || 0));
    },
    resetForm() {
      this.toEmails = [];
      this.ccEmails = [];
      const rawSubject = (this.emailTemplate && this.emailTemplate.subject) || '';
      const rawBody = (this.emailTemplate && this.emailTemplate.body) || DEFAULT_EMAIL_BODY;
      this.subject = this.replacePlaceholders(rawSubject);
      this.emailBody = this.replacePlaceholders(rawBody);
      this.toError = '';
      this.ccError = '';
      this.subjectError = '';
    },
    removeToEmail(index) {
      this.toEmails.splice(index, 1);
    },
    removeCcEmail(index) {
      this.ccEmails.splice(index, 1);
    },
    handleCancel() {
      this.$emit('input', false);
    },
    handleSend() {
      const isEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
      if (this.toEmails.length === 0) {
        this.toError = 'At least one recipient is required';
        return;
      }
      const invalidTo = this.toEmails.filter(e => !isEmail(e));
      if (invalidTo.length > 0) {
        this.toError = `Invalid email address: ${invalidTo[0]}`;
        return;
      }
      const invalidCc = this.ccEmails.filter(e => !isEmail(e));
      if (invalidCc.length > 0) {
        this.ccError = `Invalid CC email address: ${invalidCc[0]}`;
        return;
      }
      if (this.subject.trim().length === 0) {
        this.subjectError = 'Subject is required';
        return;
      }
      this.toError = '';
      this.ccError = '';
      this.subjectError = '';
      if (!this.isFormValid) return;
      this.$emit('send', {
        to: [...this.toEmails],
        cc: [...this.ccEmails],
        subject: this.subject,
        body: this.emailBody,
        selectedItems: this.selectedItems,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './SummaryEmailDialog.scss';
</style>
